/**
 * VotingAgendaEditor — agenda/ballot management for a session.
 *
 * Draft/scheduled: full CRUD + reorder.
 * Live meeting sessions: item controls (Show → Open w/ countdown → Extend →
 * Close) so the host can drive the vote from the ops console while the room
 * watches the shared screen. Async sessions expose no per-item controls — the
 * window governs all items at once.
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/agenda-and-voter-roll.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting/agenda-and-voter-roll)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  Eye,
  FileInput,
  FileText,
  Gavel,
  Loader2,
  Pencil,
  Play,
  Plus,
  PlusCircle,
  Sparkles,
  Square,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  closeItem,
  createItem,
  createItems,
  deleteItem,
  extendItem,
  listBallotForms,
  openItem,
  parseAgendaDocument,
  reorderItems,
  showItem,
  updateItem,
  type AgendaParseResponse,
  type BallotFormSummary,
  type ItemInput,
  type VotingItem,
  type VotingSession,
} from './votingApi';
import { formConfigToBallotItems } from '@/lib/voting/ballotFormAdapter';
import {
  useWorkflowTemplateEnabled,
  DIGITAL_VOTING_TEMPLATE_KEY,
} from '@/app/client-zone/modules/total-operations/hooks/useWorkflowTemplateEnabled';
import {
  Badge,
  ITEM_STATUS_META,
  ITEM_TYPE_LABELS,
  OutcomeBadge,
  formatDateTime,
} from './shared';
import type { VotingItemType, VotingThreshold } from '@/lib/voting/types';
import { notify } from '@/lib/notifications';
import UserDocsLink from '@/components/shared/UserDocsLink';

interface Props {
  session: VotingSession;
  items: VotingItem[];
  canManage: boolean;
  onChanged: () => void;
}

const inputCls =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const labelCls = 'mb-1 block text-xs font-medium text-gray-600';

function tallySummary(item: VotingItem): string {
  const parts = item.options
    .map((o) => `${o.key} ${item.tally?.[o.key] ?? 0}`)
    .join(' · ');
  const abstain = item.tally?.abstain ?? 0;
  return abstain ? `${parts} · abstain ${abstain}` : parts;
}

// ── Item editor modal ───────────────────────────────────────────────────────

function ItemModal({
  item,
  defaultTimer,
  defaultRule,
  defaultCustomPct,
  onClose,
  onSave,
}: {
  item: VotingItem | null;
  defaultTimer: number | null;
  /** Session-level pass requirement (modal's majority_rule) — prefill for
   *  new items; an explicit item threshold always wins. */
  defaultRule?: VotingThreshold['rule'];
  defaultCustomPct?: number | null;
  onClose: () => void;
  onSave: (input: ItemInput) => Promise<void>;
}) {
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [itemType, setItemType] = useState<VotingItemType>(item?.item_type ?? 'resolution');
  const [options, setOptions] = useState<{ key: string; label: string }[]>(
    item?.options.map((o) => ({ key: o.key, label: o.label })) ?? [
      { key: 'A', label: 'Approve' },
      { key: 'B', label: 'Reject' },
    ],
  );
  const [maxChoices, setMaxChoices] = useState(String(item?.max_choices ?? 1));
  const [timer, setTimer] = useState(
    item?.timer_seconds != null ? String(item.timer_seconds) : defaultTimer != null ? String(defaultTimer) : '',
  );
  const [rule, setRule] = useState<VotingThreshold['rule']>(
    item?.threshold?.rule ?? defaultRule ?? 'simple',
  );
  const [customPct, setCustomPct] = useState(
    item?.threshold?.custom_threshold_pct != null
      ? String(item.threshold.custom_threshold_pct)
      : defaultCustomPct != null
        ? String(defaultCustomPct)
        : '75',
  );
  const [requiresQuorum, setRequiresQuorum] = useState(item?.threshold?.requires_quorum === true);
  const [saving, setSaving] = useState(false);

  const isRace = itemType === 'election' || itemType === 'poll';
  const nextKey = () => String.fromCharCode(65 + options.length);

  const valid =
    title.trim().length > 0 &&
    options.length >= 2 &&
    options.every((o) => o.label.trim().length > 0) &&
    new Set(options.map((o) => o.key)).size === options.length;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      const threshold: VotingThreshold = { rule };
      if (rule === 'custom') threshold.custom_threshold_pct = Number(customPct) || 50;
      if (requiresQuorum) threshold.requires_quorum = true;
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        item_type: itemType,
        options: options.map((o) => ({ key: o.key, label: o.label.trim() })),
        max_choices: Math.min(Math.max(1, Number(maxChoices) || 1), options.length),
        timer_seconds: timer ? Number(timer) : null,
        threshold,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">
            {item ? 'Edit Agenda Item' : 'New Agenda Item'}
          </h3>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div>
            <label className={labelCls}>Title *</label>
            <input
              className={inputCls}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Approve the 2026/27 budget"
              autoFocus
            />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              className={`${inputCls} resize-none`}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Item type</label>
              <select
                className={inputCls}
                value={itemType}
                onChange={(e) => setItemType(e.target.value as VotingItemType)}
              >
                <option value="resolution">Resolution</option>
                <option value="motion">Motion</option>
                <option value="budget">Budget</option>
                <option value="election">Election</option>
                <option value="poll">Poll</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Countdown (sec)</label>
              <input
                type="number"
                min={5}
                className={inputCls}
                value={timer}
                onChange={(e) => setTimer(e.target.value)}
                placeholder="Host closes manually"
              />
            </div>
          </div>

          {/* Options */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-600">Options *</label>
              <button
                type="button"
                onClick={() => setOptions((o) => [...o, { key: nextKey(), label: '' }])}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                <PlusCircle className="h-3.5 w-3.5" /> Add option
              </button>
            </div>
            <div className="space-y-1.5">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className={`${inputCls} w-14 text-center font-mono`}
                    value={opt.key}
                    maxLength={8}
                    onChange={(e) =>
                      setOptions((o) =>
                        o.map((x, j) => (j === i ? { ...x, key: e.target.value.toUpperCase() } : x)),
                      )
                    }
                  />
                  <input
                    className={inputCls}
                    value={opt.label}
                    placeholder={isRace ? 'Candidate name' : 'Option label'}
                    onChange={(e) =>
                      setOptions((o) => o.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                    }
                  />
                  <button
                    type="button"
                    disabled={options.length <= 2}
                    onClick={() => setOptions((o) => o.filter((_, j) => j !== i))}
                    className="rounded p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              {isRace
                ? 'The leading candidate/option must cross the threshold; a tie for first reports "tied".'
                : 'The first option is the affirmative (e.g. "Approve") — it must cross the threshold to pass.'}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Pass rule</label>
              <select
                className={inputCls}
                value={rule}
                onChange={(e) => setRule(e.target.value as VotingThreshold['rule'])}
              >
                <option value="simple">Simple majority</option>
                <option value="two_thirds">Two-thirds</option>
                <option value="custom">Custom %</option>
              </select>
            </div>
            {rule === 'custom' && (
              <div>
                <label className={labelCls}>Threshold %</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  className={inputCls}
                  value={customPct}
                  onChange={(e) => setCustomPct(e.target.value)}
                />
              </div>
            )}
            <div>
              <label className={labelCls}>Max choices</label>
              <input
                type="number"
                min={1}
                max={options.length}
                className={inputCls}
                value={maxChoices}
                onChange={(e) => setMaxChoices(e.target.value)}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={requiresQuorum}
              onChange={(e) => setRequiresQuorum(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            Refuse to open below quorum
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!valid || saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : item ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Agenda import modal ─────────────────────────────────────────────────────
// Two sources: a designed ballot/poll form (snapshot conversion), or an
// uploaded document / pasted text parsed server-side (heuristic → AI).

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

const ITEM_TYPE_OPTIONS: Array<{ value: VotingItemType; label: string }> = [
  { value: 'resolution', label: 'Resolution' },
  { value: 'motion', label: 'Motion' },
  { value: 'election', label: 'Election' },
  { value: 'poll', label: 'Poll' },
  { value: 'budget', label: 'Budget' },
];

function BallotImportModal({
  session,
  onClose,
  onImported,
}: {
  session: VotingSession;
  onClose: () => void;
  onImported: () => void;
}) {
  const [source, setSource] = useState<'document' | 'form'>('document');

  // ── Form source ──
  const [forms, setForms] = useState<BallotFormSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [itemType, setItemType] = useState<VotingItemType>('resolution');

  // ── Document source ──
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [docFile, setDocFile] = useState<{ name: string; data: string } | null>(null);
  const [docText, setDocText] = useState('');
  const [docMode, setDocMode] = useState<'auto' | 'heuristic' | 'ai'>('auto');
  const [docParsing, setDocParsing] = useState(false);
  const [docResult, setDocResult] = useState<AgendaParseResponse | null>(null);
  const [docItems, setDocItems] = useState<ItemInput[]>([]);

  const [importing, setImporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listBallotForms()
      .then((list) => {
        if (cancelled) return;
        setForms(list);
        if (list.length > 0) setSelectedKey(list[0].form_key);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load forms');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = forms?.find((f) => f.form_key === selectedKey) ?? null;

  // Snapshot conversion runs on selection change — the imported items are
  // plain copies of the form's choice fields at this moment in time.
  const preview = useMemo(
    () =>
      selected
        ? formConfigToBallotItems(selected, {
            defaultItemType: itemType,
            defaultTimerSeconds: session.settings.default_timer_seconds ?? null,
          })
        : { items: [], errors: [], skipped: [] },
    [selected, itemType, session.settings.default_timer_seconds],
  );

  const formCanImport = preview.items.length > 0 && preview.errors.length === 0;
  const docCanImport = docItems.length > 0;
  const canImport = source === 'form' ? formCanImport : docCanImport;
  const importCount = source === 'form' ? preview.items.length : docItems.length;

  const pickFile = async (file: File | null | undefined) => {
    if (!file) return;
    try {
      const data = await readFileAsDataUrl(file);
      setDocFile({ name: file.name, data });
      setDocText('');
      setDocResult(null);
      setDocItems([]);
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Could not read the file');
    }
  };

  const parseDocument = async () => {
    if (docParsing) return;
    if (!docFile && !docText.trim()) {
      notify.error('Choose a document or paste the agenda text first');
      return;
    }
    setDocParsing(true);
    try {
      const result = await parseAgendaDocument(
        session.id,
        docFile
          ? { file_name: docFile.name, file_data: docFile.data, mode: docMode }
          : { text: docText, mode: docMode },
      );
      setDocResult(result);
      setDocItems(result.items.map((i) => ({ ...i })));
      if (result.items.length === 0) {
        notify.info('No voting items were found in the document — see the notes below');
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Document parse failed');
    } finally {
      setDocParsing(false);
    }
  };

  const updateDocItem = (index: number, patch: Partial<ItemInput>) => {
    setDocItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const submit = async () => {
    if (!canImport || importing) return;
    setImporting(true);
    try {
      const items = source === 'form' ? preview.items : docItems;
      await createItems(session.id, items);
      notify.success(
        `Imported ${items.length} agenda item${items.length === 1 ? '' : 's'}` +
          (source === 'form' ? ` from "${selected?.title}"` : docFile ? ` from "${docFile.name}"` : ''),
      );
      onImported();
      onClose();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <Gavel className="h-5 w-5 text-blue-600" /> Import Agenda
          </h3>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Source switch */}
        <div className="flex gap-1 border-b border-gray-200 px-5 py-2">
          {(
            [
              ['document', 'From document', FileText],
              ['form', 'From form', Gavel],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setSource(key)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${
                source === key ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {source === 'document' && (
            <>
              <div>
                <label className={labelCls}>Agenda / ballot document</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.docx,.odt,.ott,.fodt,.txt,.md"
                    className="hidden"
                    onChange={(e) => void pickFile(e.target.files?.[0])}
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-blue-300 bg-blue-50/50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                  >
                    <Upload className="h-4 w-4" />
                    {docFile ? docFile.name : 'Choose DOCX, PDF, ODT or TXT…'}
                  </button>
                  {docFile && (
                    <button
                      onClick={() => {
                        setDocFile(null);
                        setDocResult(null);
                        setDocItems([]);
                        if (fileRef.current) fileRef.current.value = '';
                      }}
                      className="text-xs text-gray-400 hover:text-red-500"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {!docFile && (
                <div>
                  <label className={labelCls}>…or paste the agenda text</label>
                  <textarea
                    className={`${inputCls} font-mono text-xs`}
                    rows={6}
                    placeholder={'1. Welcome\n2. Approval of previous minutes\n3. Election of trustees — Jane Doe, John Smith\n4. Special resolution: approve the levy increase (75% majority)'}
                    value={docText}
                    onChange={(e) => setDocText(e.target.value)}
                  />
                </div>
              )}

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className={labelCls}>Parsing mode</label>
                  <select
                    className={inputCls}
                    value={docMode}
                    onChange={(e) => setDocMode(e.target.value as 'auto' | 'heuristic' | 'ai')}
                  >
                    <option value="auto">Auto — numbered list first, AI when needed</option>
                    <option value="heuristic">Structured only — no AI</option>
                    <option value="ai">AI only — free-form documents</option>
                  </select>
                </div>
                <button
                  onClick={() => void parseDocument()}
                  disabled={docParsing || (!docFile && !docText.trim())}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {docParsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {docParsing ? 'Parsing…' : 'Parse document'}
                </button>
              </div>

              {docResult && docResult.warnings.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <ul className="list-disc space-y-0.5 pl-4 text-xs text-amber-700">
                    {docResult.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {docResult && docResult.source === 'ai' && (
                <p className="flex items-center gap-1 text-[11px] text-violet-600">
                  <Sparkles className="h-3 w-3" /> Structured by AI — review each item before importing.
                </p>
              )}

              {docItems.length > 0 && (
                <div>
                  <p className={labelCls}>
                    {docItems.length} voting item{docItems.length === 1 ? '' : 's'} — edit or remove before importing
                  </p>
                  <div className="space-y-1.5">
                    {docItems.map((item, i) => (
                      <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="flex items-start gap-2">
                          <span className="mt-2 w-5 shrink-0 text-center text-xs font-semibold text-gray-400">
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1 space-y-1.5">
                            <input
                              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium focus:border-blue-500 focus:outline-none"
                              value={item.title}
                              onChange={(e) => updateDocItem(i, { title: e.target.value })}
                            />
                            <div className="flex items-center gap-2">
                              <select
                                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
                                value={item.item_type ?? 'resolution'}
                                onChange={(e) =>
                                  updateDocItem(i, { item_type: e.target.value as VotingItemType })
                                }
                              >
                                {ITEM_TYPE_OPTIONS.map((t) => (
                                  <option key={t.value} value={t.value}>
                                    {t.label}
                                  </option>
                                ))}
                              </select>
                              <p className="truncate text-xs text-gray-500">
                                {(item.options ?? []).map((o) => `${o.key}: ${o.label}`).join('  ·  ')}
                                {(item.max_choices ?? 1) > 1 && ` · pick up to ${item.max_choices}`}
                                {item.threshold?.rule === 'two_thirds' && ' · two-thirds'}
                                {item.threshold?.rule === 'custom' &&
                                  ` · ${item.threshold.custom_threshold_pct}%`}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setDocItems((prev) => prev.filter((_, j) => j !== i))}
                            className="mt-1 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                            title="Remove this item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {source === 'form' && (
            <>
              {loadError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {loadError}
                </div>
              )}
              {forms === null && !loadError && (
                <p className="py-8 text-center text-sm text-gray-400">Loading ballot forms…</p>
              )}
              {forms !== null && forms.length === 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center">
                  <p className="text-sm font-medium text-gray-600">No ballot or poll forms found</p>
                  <p className="mt-1 text-xs text-gray-400">
                    Design a form with the &quot;Ballot&quot; purpose first, then import it here.
                  </p>
                </div>
              )}

              {forms !== null && forms.length > 0 && (
                <>
                  <div>
                    <label className={labelCls}>Source form</label>
                    <select
                      className={inputCls}
                      value={selectedKey}
                      onChange={(e) => setSelectedKey(e.target.value)}
                    >
                      {forms.map((f) => (
                        <option key={f.form_key} value={f.form_key}>
                          {f.title || f.form_key} ({f.form_purpose || 'form'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls}>Import items as</label>
                    <select
                      className={inputCls}
                      value={itemType}
                      onChange={(e) => setItemType(e.target.value as VotingItemType)}
                    >
                      <option value="resolution">Resolutions (For/Against threshold)</option>
                      <option value="motion">Motions</option>
                      <option value="election">Election items (leading option wins)</option>
                      <option value="poll">Poll items</option>
                    </select>
                  </div>

                  {preview.errors.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                      <p className="mb-1 text-xs font-semibold text-red-700">
                        This form cannot be imported — fix these fields first:
                      </p>
                      <ul className="list-disc space-y-0.5 pl-4 text-xs text-red-600">
                        {preview.errors.map((e, i) => (
                          <li key={i}>
                            <span className="font-medium">{e.field_label}:</span> {e.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {preview.items.length > 0 && (
                    <div>
                      <p className={labelCls}>
                        {preview.items.length} voting item{preview.items.length === 1 ? '' : 's'} will be added
                      </p>
                      <div className="space-y-1.5">
                        {preview.items.map((item, i) => (
                          <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <p className="text-sm font-medium text-gray-800">
                              {i + 1}. {item.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              {item.options.map((o) => `${o.key}: ${o.label}`).join('  ·  ')}
                              {item.max_choices > 1 && ` · pick up to ${item.max_choices}`}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.skipped.length > 0 && (
                    <p className="text-xs text-gray-400">
                      {preview.skipped.length} non-votable field{preview.skipped.length === 1 ? '' : 's'} skipped
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3">
          <p className="text-[11px] text-gray-400">
            {source === 'form'
              ? 'Snapshot import — later form edits won\u2019t change this agenda.'
              : 'Imported items are plain agenda entries — review wording after import.'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => void submit()}
              disabled={!canImport || importing}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {importing ? 'Importing…' : `Import ${importCount} Item${importCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main editor ─────────────────────────────────────────────────────────────

export default function VotingAgendaEditor({ session, items, canManage, onChanged }: Props) {
  const [editing, setEditing] = useState<VotingItem | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Ballot authoring via the form designer requires the Digital Voting
  // workflow — hidden (fail-closed while loading) unless the client has it
  // enabled via IndustrySolutionSettings, through whichever solution provides
  // it (standalone Digital Voting or Real Estate & Property Management).
  const { enabled: digitalVotingActive } = useWorkflowTemplateEnabled(DIGITAL_VOTING_TEMPLATE_KEY);

  const editable = canManage && ['draft', 'scheduled'].includes(session.status);
  const isLiveMeeting = session.delivery_mode === 'meeting' && session.status === 'live';
  const defaultTimer = session.settings.default_timer_seconds ?? null;

  const sorted = useMemo(() => [...items].sort((a, b) => a.seq - b.seq), [items]);

  const run = async (id: number, fn: () => Promise<unknown>) => {
    setBusyId(id);
    try {
      await fn();
      onChanged();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= sorted.length) return;
    const order = sorted.map((i) => i.id);
    [order[index], order[target]] = [order[target], order[index]];
    try {
      await reorderItems(session.id, order);
      onChanged();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Reorder failed');
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {sorted.length === 0
            ? 'No agenda items yet — add resolutions, motions, elections or polls.'
            : `${sorted.length} item${sorted.length === 1 ? '' : 's'} on the ballot`}
          <UserDocsLink docsPath="/total-operations/workflows/digital-voting/agenda-and-voter-roll#the-agenda-tab--building-the-ballot" label="User guide: Agenda and voter roll" className="ml-1 align-middle" />
        </p>
        {editable && (
          <div className="flex items-center gap-2">
            {digitalVotingActive && (
              <>
                <a
                  href="/client-zone/modules/total-forms?tab=builder&purpose=ballot"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                >
                  <Gavel className="h-4 w-4" /> Design in Forms
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <button
                  onClick={() => setImporting(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                >
                  <FileInput className="h-4 w-4" /> Import Agenda
                </button>
              </>
            )}
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> Add Item
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {sorted.map((item, idx) => {
          const statusMeta = ITEM_STATUS_META[item.status];
          const pending = item.status === 'pending';
          return (
            <div
              key={item.id}
              className={`rounded-xl border p-3 ${
                item.status === 'open'
                  ? 'border-emerald-300 bg-emerald-50/40'
                  : item.status === 'showing'
                    ? 'border-blue-300 bg-blue-50/40'
                    : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                {editable && (
                  <div className="flex flex-col">
                    <button
                      onClick={() => void move(idx, -1)}
                      disabled={idx === 0}
                      className="rounded p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => void move(idx, 1)}
                      disabled={idx === sorted.length - 1}
                      className="rounded p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <span className="mt-0.5 w-6 shrink-0 text-center text-sm font-semibold text-gray-400">
                  {item.seq}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{item.title}</span>
                    <Badge
                      label={ITEM_TYPE_LABELS[item.item_type] ?? item.item_type}
                      color="#4b5563"
                      bg="#f3f4f6"
                    />
                    <span className="text-xs font-medium" style={{ color: statusMeta.color }}>
                      {statusMeta.label}
                    </span>
                    <OutcomeBadge outcome={item.outcome} />
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {item.options.map((o) => `${o.key}: ${o.label}`).join('  ·  ')}
                    {item.timer_seconds != null && ` · ${item.timer_seconds}s timer`}
                    {item.max_choices > 1 && ` · pick up to ${item.max_choices}`}
                  </p>
                  {(item.status === 'closed' || item.status === 'open') && item.tally?.total != null && (
                    <p className="mt-0.5 text-xs text-gray-500">
                      {tallySummary(item)}
                      {item.status === 'open' && item.ends_at && ` · ends ${formatDateTime(item.ends_at)}`}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex shrink-0 items-center gap-1">
                  {isLiveMeeting && canManage && (
                    <>
                      {pending && (
                        <button
                          onClick={() => void run(item.id, () => showItem(item.id))}
                          disabled={busyId === item.id}
                          title="Show to participants (not yet votable)"
                          className="inline-flex items-center gap-1 rounded-lg border border-blue-300 px-2 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        >
                          <Eye className="h-3.5 w-3.5" /> Show
                        </button>
                      )}
                      {(pending || item.status === 'showing' || item.status === 'closed') && (
                        <button
                          onClick={() => void run(item.id, () => openItem(item.id))}
                          disabled={busyId === item.id}
                          title={
                            item.status === 'closed'
                              ? 'Re-open for a revote'
                              : 'Open for voting (starts countdown)'
                          }
                          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <Play className="h-3.5 w-3.5" /> {item.status === 'closed' ? 'Revote' : 'Open'}
                        </button>
                      )}
                      {item.status === 'open' && (
                        <>
                          <button
                            onClick={() => void run(item.id, () => extendItem(item.id, 60))}
                            disabled={busyId === item.id}
                            title="Add 60 seconds"
                            className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-2 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                          >
                            <Clock className="h-3.5 w-3.5" /> +60s
                          </button>
                          <button
                            onClick={() => void run(item.id, () => closeItem(item.id))}
                            disabled={busyId === item.id}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-300 px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            <Square className="h-3.5 w-3.5" /> Close & Tally
                          </button>
                        </>
                      )}
                    </>
                  )}
                  {editable && pending && (
                    <>
                      <button
                        onClick={() => setEditing(item)}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        title="Edit item"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => void run(item.id, () => deleteItem(item.id))}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        title="Delete item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(adding || editing) && (
        <ItemModal
          item={editing}
          defaultTimer={defaultTimer}
          defaultRule={session.settings.majority_rule}
          defaultCustomPct={session.settings.custom_threshold_pct ?? null}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSave={async (input) => {
            if (editing) {
              await updateItem(editing.id, input);
              notify.success('Item updated');
            } else {
              await createItem(session.id, input);
              notify.success('Item added to the ballot');
            }
            setAdding(false);
            setEditing(null);
            onChanged();
          }}
        />
      )}

      {importing && (
        <BallotImportModal
          session={session}
          onClose={() => setImporting(false)}
          onImported={onChanged}
        />
      )}
    </div>
  );
}
